import React, { ReactNode } from "react";
import ApiService, { ApiConnectionError, ApiError } from "../api/api.service";
import { ExecutionSchedule } from "@models/executionSchedule";
import AuthService from "@services/auth/auth.service";


interface FeatureFlags {
  [key: string]: boolean;
}

export enum Backends { 
  Snowflake = "SNOWFLAKE",
  Pliable = "PLIABLE"
} 

export interface FormatLocale {
  decimal: string;
  thousands: string;
  grouping: number[];
  currency: [string, string];
}

export interface TenantInfra {
  is_ready: boolean;
  snowpipe: {
    setup_complete: boolean;
    setup_error: string;
  }
  plb_tracker?: {
    api_endpoint: string;
  }
}

export interface SurveyDataSources {
  selections: string[];
  otherCRM: string;
  otherERP: string;
  otherLMS: string;
  otherOutreachTool: string;
  otherAnalyticsTool: string;
  otherProductDatabase: string;
  otherProductivityTool: string;
  otherAdPlatform: string;
  otherAccountingTool: string;
  otherSellingPlatform: string;
  otherHRPlatform: string;
  otherAffiliatePlatform: string;
  otherSupportTool: string;
  otherSportsbook: string;
  otherSEO: string;
  otherPAM: string;
}

export interface SurveyAnswers {
  dataTeamSize?: string;
  existingTools?: string[];
  role?: string;
  otherRole?: string;
  sampleData?: boolean;
  onboardingEventLink?: string;
  onboardingInviteeLink?: string;

  database?: {
    hasWarehouse?: boolean;
    hasEtl?: boolean;
    warehouse: string;
    otherWarehouse?: string;
    etl: string;
    selfServicePreference?: string;
    onboardingEventLink?: string;
    onboardingInviteeLink?: string;
    usesDBT?: boolean;
  },
  businessType?: {
      products: boolean;
      referrals: boolean;
      services: boolean;
      assets: boolean;
  };
  industry?: {
      selection: string;
      otherValue: string;
  };
  customers?: {
      smbs: boolean;
      enterprises: boolean;
      consumers: boolean;
  };
  useCases?: {
      selections: string[];
      otherValue: string;
  };
  personal?: {
    isTechnical?: boolean;
  };
  dataSources?: SurveyDataSources;


  snowflakeUseCase?: string;
  wantsPliableHostedSnowflake?: boolean;
  pliableUseCase?: {
    selections: string[];
    otherValue: string;
  }
};

export interface HostedSuperset {
  created_at: string;
  db_name: string;
};

export interface BillingAccountRef {
  billing_account_id: string;
  status: string;
};

export interface Whitelabeling {
  logo_path?: string;
  primary_cta_color?: string;
  brand_color?: string;
  brand_dark_color?: string;
};


// Snake case here to be interoperable with the back end
export interface Config {
  fivetran_destination_id: string;
  fivetran_group_id: string;
  flags: FeatureFlags;
  id: string;
  io_bucket: string;
  short_id: string;
  tenant_slug: string;
  tenant_uuid: string;
  infra: TenantInfra;
  is_disabled: boolean;
  database?: Backends;
  last_used_template?: string;
  last_used_template_done_at?: string;
  build_schedule: ExecutionSchedule;
  github_installation_id: string;
  github_repo_name: string;
  git_source?: string;
  git_default_branch?: string;
  pliable_repo_meta?: any;
  onboarding_info?: SurveyAnswers;
  format_locale?: FormatLocale;
  superset?: HostedSuperset;
  billing?: BillingAccountRef;
  subscription_status?: string;
  days_left_in_trial?: number;
  dbt_variables?: {[key: string]: string};
  whitelabeling?: Whitelabeling;

  use_snowflake_proxy?: boolean;
}

export interface UserConfig {
  frontegg_id: string;
  current_branch: string;
  dbt_schema_prefix: string;
  has_github_access: boolean;
  agreed_to_terms_date?: number;
  favorite_node_ids?: string[];
  favorite_analysis_ids?: string[];
  favorite_dashboard_ids?: string[];
}


// A "provider" is used to encapsulate only the
// components that needs the state in this context


interface Props {
  children: ReactNode;
}

interface State {
  loading: boolean;
  config: Config|null;
}

export default class ConfigService {
  private static instance: ConfigService;

  config: Config|null;
  userConfig: UserConfig|null;
  loading: boolean;
  originalCssVars: {[key: string]: string};


  public static getInstance(): ConfigService {
    if (!ConfigService.instance) {
        ConfigService.instance = new ConfigService();
    }

    return ConfigService.instance;
  }

  constructor() {
    this.config = null;
    this.userConfig = null;
    this.loading = true;
    this.originalCssVars = {};
    this.captureOriginalCssVars(['--brand-blue', '--pliable-navy', '--brand-orange']);
  }

  public async loadUserConfig() {
    const userConfig = await ApiService.getInstance().request('GET', '/user-config') as UserConfig;
    this.userConfig = userConfig;

  }

  public async updateFormatLocale(newFormatLocale: FormatLocale) {
    if (this.config) {
        this.config.format_locale = newFormatLocale;
    }
}

  public async loadConfig() {
    // Wait for the auth service to have an auth state
    const authState = AuthService.getInstance().getState();
    while (!authState.plbAuthState) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    try {
      const data = await ApiService.getInstance().request('GET', '/config');
      this.config = data as Config;
      this.applyWhitelabeling();
    } catch(ex) {

      if(ex instanceof ApiConnectionError){
        window.location.href= '/unable-to-connect';
        return;
      }else if (ex instanceof ApiError && ex.code === 404) {
        // Tenant does not exist 
        window.location.href= '/tenant-not-found';
        return;
      }
      console.error(ex);
    }
  }

  private captureOriginalCssVars(varNames: string[]) {
    const root = getComputedStyle(document.documentElement);
    varNames.forEach(varName => {
      this.originalCssVars[varName] = root.getPropertyValue(varName).trim();
    });
  }

  private applyWhitelabeling() {
    // override styles with whitelabeling if present
      if (this.config?.whitelabeling) {
        const whitelabeling = this.config.whitelabeling;
        document.documentElement.style.setProperty('--brand-blue', (whitelabeling.brand_color) ? whitelabeling.brand_color : this.originalCssVars['--brand-blue']);
        document.documentElement.style.setProperty('--brand-orange', (whitelabeling.primary_cta_color) ? whitelabeling.primary_cta_color : this.originalCssVars['--brand-orange']);
        document.documentElement.style.setProperty('--pliable-navy', (whitelabeling.brand_dark_color) ? whitelabeling.brand_dark_color : this.originalCssVars['--pliable-navy']);
      }
  }

  private redirectToOnboarding() {
    if (AuthService.getInstance().authState?.isAuthenticated) {
      // Check for the redirect
      const returnPath = localStorage.getItem('returnPath');
      
      if (returnPath) {
        // We're redirecting so remove it from local storage so they're not caught in a loop
        localStorage.removeItem('returnPath');
      }
      if (returnPath && (window.location.pathname + window.location.search) != returnPath) {
        window.location.href = `${returnPath}#returning`;
      } else if (!returnPath && !window.location.pathname.startsWith('/onboarding')) {
        window.location.href = '/onboarding';
      }

    } else if (!window.location.pathname.startsWith('/login')) {
      window.location.href = '/login';
    }
  }

  
}

